import { getCurrentUserOptions } from "@/lib/data/react-query";
import type {
  ActionPermissionKey,
  ActionPermissionValue,
} from "@/lib/data/static/permissionOptions";
import type { User } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { ChatMessageQueueWorker } from "@/components/features/ai-chat/ChatMessageQueueWorker";

type UserProviderProps = {
  children: React.ReactNode;
  defaultUser?: User;
  storageKey?: string;
};

type UserProviderState = {
  currentUser: User;
  hasActionPermission: (
    permissionId: ActionPermissionKey,
    action: ActionPermissionValue,
  ) => boolean;
  hasPagePermission: (permissionId: string) => boolean;
  hasAnyPagePermission: (permissionIds: readonly string[]) => boolean;
  hasGrant: (permissionId: string, action: string) => boolean;
};

interface UserProviderHmrData {
  userProviderContext?: React.Context<UserProviderState | null>;
}

const userProviderHmrData = import.meta.hot?.data as
  | UserProviderHmrData
  | undefined;

const UserProviderContext =
  userProviderHmrData?.userProviderContext ??
  createContext<UserProviderState | null>(null);

if (import.meta.hot) {
  const hotData = import.meta.hot.data as UserProviderHmrData | undefined;
  if (hotData) {
    hotData.userProviderContext = UserProviderContext;
  }
}

export function UserProvider({ children, ...props }: UserProviderProps) {
  const { data: currentUserData } = useSuspenseQuery(getCurrentUserOptions());
  const currentUser = currentUserData.data;

  const hasActionPermission = (
    permissionId: ActionPermissionKey,
    action: ActionPermissionValue,
  ) => {
    if (currentUser.owner) return true;

    return currentUser.effectiveGrants.some(
      (grant) => grant.permissionId === permissionId && grant.action === action,
    );
  };

  const hasPagePermission = (permissionId: string) => {
    if (currentUser.owner) return true;

    return currentUser.effectiveGrants.some(
      (grant) => grant.permissionId === permissionId && grant.action === "view",
    );
  };

  const hasGrant = (permissionId: string, action: string) => {
    if (currentUser.owner) return true;

    return currentUser.effectiveGrants.some(
      (grant) => grant.permissionId === permissionId && grant.action === action,
    );
  };

  const hasAnyPagePermission = (permissionIds: readonly string[]) =>
    permissionIds.some(hasPagePermission);

  return (
    <ErrorBoundary fallback={() => <div>Something went wrong</div>}>
      <UserProviderContext.Provider
        {...props}
        value={{
          currentUser,
          hasActionPermission,
          hasPagePermission,
          hasAnyPagePermission,
          hasGrant,
        }}
      >
        <ChatMessageQueueWorker
          scopeKey={JSON.stringify([currentUser.firmId, currentUser.id])}
        />
        {children}
      </UserProviderContext.Provider>
    </ErrorBoundary>
  );
}

export const useUser = () => {
  const context = useContext(UserProviderContext);

  if (context === null) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};

export const useOptionalUser = () => {
  return useContext(UserProviderContext);
};
